import type { User } from '@/types/user';
import { apiFetch } from '@/lib/api';

export class AuthService {
  private readonly baseUrl: string = '';

  async register(input: { email: string; password: string; fullName: string }): Promise<User> {
    const res = await apiFetch(`/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // skipRefresh not necessary for register
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Registration failed');
    const data = await res.json();
    return data.data as User;
  }

  async login(input: { email: string; password: string; rememberMe?: boolean }): Promise<User> {
    const res = await apiFetch(`/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Login failed');
    const data = await res.json();
    return data.data as User;
  }

  async logout(): Promise<void> {
    const res = await apiFetch(`/api/auth/logout`, { method: 'POST', skipRefresh: true });
    if (!res.ok) throw new Error('Logout failed');
  }

  async refresh(): Promise<User> {
    const res = await apiFetch(`/api/auth/refresh`, { method: 'POST', skipRefresh: true });
    if (!res.ok) throw new Error('Refresh failed');
    const data = await res.json();
    return data.data as User;
  }

  async me(skipRedirect = false): Promise<User> {
    const res = await apiFetch(`/api/auth/me`, { skipRedirect });
    if (!res.ok) throw new Error('Not authenticated');
    const data = await res.json();
    return data.data as User;
  }

  async forgotPassword(email: string): Promise<{ debugToken?: string }> {
    const res = await apiFetch(`/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify({ email }),
    });
    if (!res.ok) throw new Error('Failed to request password reset');
    const data = await res.json().catch(() => ({ data: {} }));
    return (data?.data || {}) as { debugToken?: string };
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const res = await apiFetch(`/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      skipRefresh: true,
      body: JSON.stringify({ token, newPassword }),
    });
    if (!res.ok) throw new Error('Failed to reset password');
  }

  async resendVerification(): Promise<{ sent: boolean }> {
    const res = await apiFetch(`/api/auth/resend-verification`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to resend verification email');
    const data = await res.json().catch(() => ({ data: {} }));
    return (data?.data || { sent: false }) as { sent: boolean };
  }
}

